import type { ClaimModel } from '@ctw/shared/api/fhir/models/claim';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { RiskGapAddToNoteButton } from '@ctw/shared/content/risk/risk-gap-add-to-note-button';
import { RiskGapCopyButton } from '@ctw/shared/content/risk/risk-gap-copy-button';
import { useRiskGapSummaryNoteContext } from '@ctw/shared/context/controlled-text-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { SYSTEM_URLS, summarizeFhirResource, type ZusFhirResource } from '@zeus-health/types';
import { capitalize } from 'lodash-es';
import type { EvidenceModel } from './use-evidence-resources';

/** Max ICD-10 codes shown on a claim evidence card before collapsing to "+N more". */
const CARD_ICD10_LIMIT = 3;

interface ClinicalEvidenceCardProps {
  resource: EvidenceModel;
  /** Quote strings extracted from the evidence reference summary. */
  quotes?: Array<string>;
  /** DocumentReference only: the resolved source document the card describes. */
  parentDocument?: DocumentModel;
  onClick?: () => void;
  showAddToNoteButton?: boolean;
}

interface ClinicalEvidenceCardContent {
  title: string;
  primary: string;
  secondary?: string;
  date?: string;
  quotes?: Array<string>;
}

type SupportedEvidenceResourceType =
  | 'Condition'
  | 'Observation'
  | 'DiagnosticReport'
  | 'DocumentReference'
  | 'MedicationStatement'
  | 'MeasureReport'
  | 'Claim';

type SupportedEvidenceModel =
  | ConditionModel
  | ObservationModel
  | DiagnosticReportModel
  | DocumentModel
  | MedicationStatementModel
  | RiskGapModel
  | ClaimModel;

interface ClinicalEvidenceHandler<TModel extends SupportedEvidenceModel> {
  resourceType: SupportedEvidenceResourceType;
  toCardContent: (
    model: TModel | undefined,
    quotes?: Array<string>,
    parentDocument?: DocumentModel,
  ) => ClinicalEvidenceCardContent;
}

const CLINICAL_EVIDENCE_HANDLERS: Record<
  SupportedEvidenceResourceType,
  ClinicalEvidenceHandler<SupportedEvidenceModel>
> = {
  Condition: {
    resourceType: 'Condition',
    toCardContent: (model) => {
      const condition = model as ConditionModel | undefined;
      if (condition?.isSummaryResource) {
        return {
          title: 'Condition',
          primary: condition.display || 'Unknown',
          secondary: condition.onset ? `Onset ${condition.onset}` : undefined,
          date: condition.recordedDate ? `Last recorded ${condition.recordedDate}` : undefined,
        };
      }
      return {
        title: condition?.isEncounterDiagnosis ? 'Encounter Diagnosis' : 'Condition',
        primary: condition?.icd10CMEnrichments[0]?.code || condition?.display || 'Unknown',
        secondary: condition?.display,
        date: condition?.recordedDate ? `Recorded ${condition.recordedDate}` : undefined,
      };
    },
  },
  Observation: {
    resourceType: 'Observation',
    toCardContent: (model) => {
      const observation = model as ObservationModel | undefined;
      return {
        title: 'Observation',
        primary: observation?.valueWithUnits || 'Unknown result',
        secondary: observation?.title || 'Observation',
        date: observation?.dateDisplay ? `Effective date ${observation.dateDisplay}` : undefined,
      };
    },
  },
  DiagnosticReport: {
    resourceType: 'DiagnosticReport',
    toCardContent: (model) => {
      const diagnosticReport = model as DiagnosticReportModel | undefined;
      return {
        title: 'Diagnostic Report',
        primary: diagnosticReport?.displayName || 'Diagnostic report',
        secondary:
          diagnosticReport?.performer ||
          diagnosticReport?.patientOrganizationName ||
          diagnosticReport?.numResultsDisplay ||
          'Diagnostic report',
        date: diagnosticReport?.dateDisplay
          ? `Effective date ${diagnosticReport.dateDisplay}`
          : undefined,
      };
    },
  },
  DocumentReference: {
    resourceType: 'DocumentReference',
    toCardContent: (model, quotes, parentDocument) => {
      const document = model as DocumentModel | undefined;
      // Clicking the card opens the parent document, so it supplies the title
      // and dates; the cited document's own title moves to the secondary line.
      const displayed = parentDocument ?? document;
      let date: string | undefined;
      if (displayed?.encounterDate && displayed.dateCreated) {
        date = `Encounter ${displayed.encounterDate}, Retrieved ${displayed.dateCreated}`;
      } else if (displayed?.encounterDate) {
        date = `Encounter ${displayed.encounterDate}`;
      } else if (displayed?.dateCreated) {
        date = `Retrieved ${displayed.dateCreated}`;
      }
      const displayedTitle = displayed?.displayTitle;
      const childTitle = document?.displayTitle;
      const primary = displayedTitle ?? childTitle ?? document?.typeDisplay ?? 'Document';
      const secondary =
        parentDocument && displayedTitle && childTitle && childTitle !== displayedTitle
          ? childTitle
          : document?.typeDisplay;
      return {
        title: 'Document',
        primary,
        secondary,
        date,
        quotes,
      };
    },
  },
  MedicationStatement: {
    resourceType: 'MedicationStatement',
    toCardContent: (model) => {
      const medication = model as MedicationStatementModel | undefined;
      return {
        title: 'Medication',
        primary: medication?.display || 'Unknown medication',
        date: medication?.lastPrescribedDate
          ? `Last prescribed ${medication.lastPrescribedDate}`
          : undefined,
      };
    },
  },
  Claim: {
    resourceType: 'Claim',
    toCardContent: (model) => {
      const claim = model as ClaimModel | undefined;
      const icd10Codes = claim?.icd10Codes ?? [];
      const serviceCodes = claim?.serviceCodes ?? [];
      // Cap the primary line at CARD_ICD10_LIMIT codes with a "+N more" suffix; the
      // full list stays in the drawer and the copy/note text.
      const cappedIcd10 =
        icd10Codes.length > CARD_ICD10_LIMIT
          ? `${icd10Codes.slice(0, CARD_ICD10_LIMIT).join(', ')} +${icd10Codes.length - CARD_ICD10_LIMIT} more`
          : icd10Codes.join(', ');
      return {
        title: 'Claim',
        primary: icd10Codes.length > 0 ? `ICD-10 ${cappedIcd10}` : 'Claim',
        secondary: serviceCodes.length > 0 ? `Service: ${serviceCodes.join(', ')}` : undefined,
        date: claim?.serviceDate ? `Service date ${claim.serviceDate}` : undefined,
      };
    },
  },
  MeasureReport: {
    resourceType: 'MeasureReport',
    toCardContent: (model) => {
      const riskGap = model as RiskGapModel | undefined;
      const isPayerGap =
        riskGap?.resource.meta?.profile?.includes(
          SYSTEM_URLS.PROFILE.PAYER_RISK_GAP_MEASURE_REPORT,
        ) ?? false;

      if (!isPayerGap) {
        return {
          title: 'Risk Gap',
          primary: 'Unsupported evidence',
        };
      }

      const icd10Codes = riskGap?.icd10Codes?.map((c) => c.code).filter(Boolean) ?? [];
      const icd10Display = icd10Codes.length > 0 ? `ICD-10 ${icd10Codes.join(', ')}` : '';
      const hccDisplay =
        riskGap?.hccCode && riskGap.hccCode !== 'N/A' ? `HCC ${riskGap.hccCode}` : '';
      return {
        title: 'Payer Risk Gap',
        primary: [hccDisplay, icd10Display].filter(Boolean).join(' · '),
        secondary: capitalize(
          riskGap?.resource.extension?.find(
            (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.PAYER_RISK_GAP.STATUS,
          )?.valueString,
        ),
        date: riskGap?.period ? `Period ${riskGap.period}` : undefined,
      };
    },
  },
};

export const ClinicalEvidenceCard = ({
  resource,
  quotes,
  parentDocument,
  onClick,
  showAddToNoteButton,
}: ClinicalEvidenceCardProps) => {
  const { setControlledText: setNote } = useRiskGapSummaryNoteContext();
  const handler =
    CLINICAL_EVIDENCE_HANDLERS[resource.resourceType as SupportedEvidenceResourceType];

  const content = handler
    ? handler.toCardContent(resource as SupportedEvidenceModel, quotes, parentDocument)
    : {
        title: resource.resourceType,
        primary: resource.id,
        secondary: resource.resourceType,
      };

  // Keep copy/note text on the same document the card describes.
  const clinicalContent = summarizeFhirResource(
    (parentDocument ?? resource).resource as ZusFhirResource,
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={tw`group relative flex cursor-pointer flex-col rounded-lg border border-border bg-card p-4 text-left transition-all hover:border-border/80 hover:shadow-sm`}
    >
      <div className={tw`mb-2 flex min-h-[16px] items-center justify-between`}>
        <div
          className={tw`font-semibold text-meta-main text-muted-foreground text-xs uppercase tracking-wider`}
        >
          {content.title}
        </div>
        <div className={tw`-mt-2 flex-shrink-0 flex-nowrap`}>
          {showAddToNoteButton ? (
            <RiskGapAddToNoteButton textToAdd={clinicalContent} addTextCallback={setNote} />
          ) : null}
          <RiskGapCopyButton copyContent={clinicalContent} />
        </div>
      </div>
      <div className={tw`flex flex-1 flex-col`}>
        <div className={tw`break-words font-semibold text-foreground text-lg leading-tight`}>
          {content.primary}
        </div>
        {content.secondary && (
          <div className={tw`mt-0.5 break-words text-muted-foreground text-sm`}>
            {content.secondary}
          </div>
        )}
        {content.quotes?.map((quote, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: quote list is render-only with no stable id and no reordering
            key={index}
            className={tw`mt-2 border-muted-foreground/30 border-l-2 pl-2 text-muted-foreground text-sm italic`}
          >
            {quote}
          </div>
        ))}
        <div className={tw`flex-1`} />
        {content.date && (
          <div className={tw`mt-auto pt-2 text-content-subtle text-xs`}>{content.date}</div>
        )}
      </div>
    </button>
  );
};
